import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import moment from 'moment';
import { getCryptoDecimals } from 'src/core/const';
import { markPaymentRefunded, getRefundState } from '../../../dashboard/services/reportingApi';
import {
  AMOUNT_DISPLAY_THRESHOLD,
  parseOptionalFloat,
  getRawSettlementUsd,
  getSettlementUsd,
  getSettlementStatus,
  getMerchantSettledUsd,
  getMerchantShortfallUsd,
  getCryptoOverpaymentUsd,
  getCryptoShortfallUsd,
  getCryptoMismatchUsd,
  getNetPayout,
  getSignedAmountClass,
  getFeeSummary,
} from '../../utils/transactionUtils';
import useFeePreview from './useFeePreview';
import useResolution from './useResolution';

// Local icon definitions (subset needed by TransactionDrawer)
const Icons = {
  X: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  ExternalLink: () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
};

// Transaction Drawer (Detail Panel)
// Refund method options for mark-refunded
const REFUND_METHOD_OPTIONS = [
  { value: 'store_credit', label: 'Store Credit' },
  { value: 'check', label: 'Check' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'ach', label: 'ACH' },
  { value: 'other', label: 'Other' },
];

function TransactionDrawer({ transaction, isOpen, onClose, user, token, onResolve }) {
  const { resolving, resolutionError, resolutionSuccess, setResolutionSuccess, handleResolve } = useResolution(transaction, token, onResolve, onClose);
  const { feePreviews, feePreviewLoading, feePreviewError } = useFeePreview(isOpen, transaction, token);

  // Mark as refunded state
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundMode, setRefundMode] = useState('manual');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [refundReference, setRefundReference] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState(null);
  const [refundSuccess, setRefundSuccess] = useState(false);
  // Query-first pattern: fetch fresh refund state from POS before showing modal
  const [refundStateLoading, setRefundStateLoading] = useState(false);
  const [fetchedRefundState, setFetchedRefundState] = useState(null);

  // Reset inline refund form state when transaction changes
  useEffect(() => {
    setRefundMethod('');
    setRefundNotes('');
    setRefundReference('');
    setRefundSubmitting(false);
    setRefundError(null);
  }, [transaction?.session_id]);

  if (!transaction) return null;

  const isSuperAdmin = user?.isSuperAdmin || user?.isSolventAdmin;
  const isFIAdmin = user?.isBankAdmin || user?.isPrimaryBankAdmin;
  const isMerchantAdmin = user?.isMerchant && !isSuperAdmin && !isFIAdmin;

  // Check if user can resolve discrepancies (merchant admin, FI admin, or super admin)
  const canResolve = isMerchantAdmin || isSuperAdmin;

  // Check if user can mark refunds (merchant admin for their own, or super/solvent admin)
  // FI Admin / ISO cannot mark refunds (they don't do operations)
  const canMarkRefund = isMerchantAdmin || isSuperAdmin;

  // Backend-provided refund state (single source of truth)
  const refundedTotalUsd = parseOptionalFloat(transaction.refunded_total_usd);
  const refundedTotalUsdSafe = refundedTotalUsd ?? 0;
  const remainingRefundable = parseOptionalFloat(transaction.remaining_refundable_usd) ?? 0;
  const refundDueUsd = parseOptionalFloat(transaction.refund_due_usd) ?? 0;
  const refundDueSource = transaction.refund_due_source || null;
  const hasRefundDue = refundDueUsd > AMOUNT_DISPLAY_THRESHOLD;
  const overageRefundRecord = (transaction.refunds || [])
    .find(r => r.refund_source === 'overage' && r.status === 'completed');
  const denialRefundRecord = (transaction.refunds || [])
    .find(r => r.refund_source === 'denial' && r.status === 'completed');
  const fetchedRefundDueUsd = parseOptionalFloat(fetchedRefundState?.refund_due_usd);
  const fetchedRemainingUsd = parseOptionalFloat(fetchedRefundState?.remaining_refundable_usd);
  const canShowRefundButton = canMarkRefund
    && ['paid', 'refund_pending'].includes(transaction.status)
    && !hasRefundDue
    && remainingRefundable > AMOUNT_DISPLAY_THRESHOLD;

  // Open refund modal with pre-filled amount (query-first pattern)
  const openRefundModal = async () => {
    setRefundMode('manual');
    setRefundMethod('');
    setRefundNotes('');
    setRefundReference('');
    setRefundError(null);
    setRefundSuccess(false);
    setFetchedRefundState(null);
    setRefundStateLoading(true);
    setRefundModalOpen(true);

    // Fetch fresh refund state from POS before showing form
    try {
      const state = await getRefundState(transaction.session_id, token);
      setFetchedRefundState(state);
      // Use fetched remaining amount instead of calculated
      const refundDue = parseFloat(state.refund_due_usd) || 0;
      const remaining = parseFloat(state.remaining_refundable_usd) || 0;
      const useRefundDue = refundDue > 0 && transaction.resolution_action === 'deny';
      setRefundAmount((useRefundDue ? refundDue : remaining).toFixed(2));
    } catch (err) {
      console.error('Failed to fetch refund state:', err);
      // Fall back to calculated amount if fetch fails
      const fallbackAmount = transaction.resolution_action === 'deny'
        ? refundDueUsd
        : remainingRefundable;
      setRefundAmount(fallbackAmount.toFixed(2));
      setRefundError('Could not fetch latest refund state. Using cached data.');
    } finally {
      setRefundStateLoading(false);
    }
  };

  const openResolutionRefundModal = () => {
    const resolutionRefundAmount = refundDueUsd > AMOUNT_DISPLAY_THRESHOLD ? refundDueUsd : refundOverageActual;
    setRefundMode('resolution');
    setRefundAmount(parseFloat(resolutionRefundAmount || 0).toFixed(2));
    setRefundMethod('');
    setRefundNotes('');
    setRefundReference('');
    setRefundError(null);
    setRefundSuccess(false);
    setRefundModalOpen(true);
  };

  // Handle refund submission
  const handleMarkRefunded = async () => {
    // Validate
    const amount = parseFloat(refundAmount);
    // Use fetched state for validation when available (query-first pattern)
    const maxAmount = refundMode === 'resolution'
      ? (fetchedRefundDueUsd && fetchedRefundDueUsd > 0
          ? fetchedRefundDueUsd
          : (refundDueUsd > 0 ? refundDueUsd : parseFloat(refundOverageActual || 0)))
      : (fetchedRefundState
          ? parseFloat(fetchedRefundState.remaining_refundable_usd) || 0
          : remainingRefundable);

    if (!amount || amount <= 0) {
      setRefundError('Please enter a valid refund amount');
      return;
    }
    // Use small tolerance for floating point comparison (1 cent)
    if (amount > maxAmount + AMOUNT_DISPLAY_THRESHOLD) {
      setRefundError(`Refund amount cannot exceed $${maxAmount.toFixed(2)}`);
      return;
    }
    if (!refundMethod) {
      setRefundError('Please select a refund method');
      return;
    }

    setRefundSubmitting(true);
    setRefundError(null);

    try {
      // Both modes now use markPaymentRefunded
      // - 'resolution' mode: Recording completion of a pending resolution refund (overage)
      // - 'manual' mode: Recording a new manual refund on a paid order
      // The resolution itself is done separately via handleResolve()
      await markPaymentRefunded(transaction.session_id, {
        // Round to 2 decimal places to avoid floating point precision issues
        refund_amount_usd: Math.round(amount * 100) / 100,
        refund_method: refundMethod,
        notes: refundNotes,
        reference_number: refundReference,
      }, token);

      setRefundSuccess(true);

      // Refresh and close after brief delay
      if (onResolve) {
        setTimeout(() => {
          onResolve();
          setRefundModalOpen(false);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setRefundError(err.message || 'Failed to mark as refunded');
    } finally {
      setRefundSubmitting(false);
    }
  };

  // Base transaction amount (merchant order amount)
  const transactionAmount = parseFloat(transaction.order_amount_usd || 0);
  const acceptedAmountUsd = parseOptionalFloat(transaction.accepted_amount_usd);
  const receivedEstimateUsd = getSettlementUsd(transaction);
  const detailAmountUsd = (() => {
    if (transaction.status === 'refunded') {
      return refundedTotalUsd ?? receivedEstimateUsd ?? acceptedAmountUsd ?? transactionAmount;
    }
    // For paid with discrepancy (accepted partial or kept overpayment), show accepted amount
    if (transaction.status === 'paid' && transaction.discrepancy_type !== 'none' && acceptedAmountUsd !== null) {
      return acceptedAmountUsd;
    }
    // For denied status, show what was received
    if (transaction.status === 'denied' && receivedEstimateUsd !== null) {
      return receivedEstimateUsd;
    }
    // For refund pending, show accepted amount
    if (transaction.status === 'refund_pending' && acceptedAmountUsd !== null) {
      return acceptedAmountUsd;
    }
    // For keep_full resolution (status becomes 'paid'), show accepted amount (full overpayment)
    if (transaction.resolution_action === 'keep_full' && acceptedAmountUsd !== null) {
      return acceptedAmountUsd;
    }
    return transactionAmount;
  })();
  const settlementStatus = getSettlementStatus(transaction);
  const merchantSettledUsd = getMerchantSettledUsd(transaction);
  const merchantShortfallUsd = getMerchantShortfallUsd(transaction);
  const showMerchantShortfall = isMerchantAdmin
    && transaction.status === 'paid'
    && settlementStatus === 'settled'
    && merchantShortfallUsd > AMOUNT_DISPLAY_THRESHOLD;
  const paymentAmountUsd = parseOptionalFloat(transaction.expected_payment_usd) ?? transactionAmount;
  const quotedTotalUsd = parseOptionalFloat(transaction.quote_rate)
    ? parseOptionalFloat(transaction.expected_crypto) * parseOptionalFloat(transaction.quote_rate)
    : null;

  // New model: crypto_matched logic uses discrepancy_type
  const cryptoMatched = transaction.discrepancy_type === 'none';
  const settlementUsd = getRawSettlementUsd(transaction);
  const cryptoOverpaymentUsd = getCryptoOverpaymentUsd(transaction);
  const cryptoShortfallUsd = getCryptoShortfallUsd(transaction);
  const cryptoMismatchUsd = getCryptoMismatchUsd(transaction);
  const discrepancyUsdRaw = parseOptionalFloat(transaction.discrepancy_usd) ?? 0;
  const resolutionOverageUsd = cryptoOverpaymentUsd > 0 ? cryptoOverpaymentUsd : Math.max(discrepancyUsdRaw, 0);
  const resolutionShortfallUsd = cryptoShortfallUsd > 0 ? cryptoShortfallUsd : Math.max(-discrepancyUsdRaw, 0);
  const resolutionAmountUsd = transaction.discrepancy_type === 'underfunded'
    ? resolutionShortfallUsd
    : resolutionOverageUsd;
  // For "refund overage" option: refund = received - expected (what customer gets back when merchant keeps expected amount)
  // Expected amount = order + customer fee (what customer agreed to pay), so we only refund the true overage
  const orderAmountUsd = parseOptionalFloat(transaction.order_amount_usd) ?? 0;
  const expectedAmountUsd = parseOptionalFloat(transaction.expected_payment_usd)
    ?? orderAmountUsd; // Fallback to order amount if no expected available
  const receivedUsd = getSettlementUsd(transaction) ?? 0;
  const refundOverageActual = receivedUsd > expectedAmountUsd ? receivedUsd - expectedAmountUsd : 0;
  // Extract fee values from POS backend
  const merchantFee = parseOptionalFloat(transaction.actual_merchant_fee_usd) ?? 0;
  const customerFee = parseOptionalFloat(transaction.actual_customer_fee_usd) ?? 0;
  const totalFees = parseOptionalFloat(transaction.actual_total_fees_usd)
    ?? (merchantFee + customerFee);
  const manualRefundedTotal = parseOptionalFloat(transaction.refunded_from_payout_usd) ?? 0;

  // Hard costs (for accounting display only - NOT used in ISO split)
  const exchangeFee = parseFloat(transaction.fee_components?.exchange_fee || transaction.exchange_fee_usd || 0);
  const bankFee = parseFloat(transaction.fee_components?.bank_fee || transaction.bank_fee_usd || 0);
  const hardCosts = exchangeFee + bankFee;

  // Buffer profit - only applies when crypto matches quote
  const bufferProfit = cryptoMatched
    ? parseFloat(transaction.buffer_realized_usd ?? 0)
    : 0;
  const marketMoveUsd = cryptoMatched
    ? parseFloat(transaction.market_move_usd ?? 0)
    : 0;

  const previewPlaceholder = {
    action: null,
    amountUsd: null,
    feePayer: null,
    merchantFee: '—',
    customerFee: '—',
    totalFees: '—',
    netPayout: '—',
    hardCosts: '—',
    refundDue: '—',
    overageRefund: '—',
  };

  const getPreviewFor = (action) => feePreviews?.[action] || previewPlaceholder;

  const requiredPreviewActions = transaction.status === 'needs_resolution'
    ? (transaction.discrepancy_type === 'underfunded'
      ? ['accept_partial', 'deny']
      : (transaction.discrepancy_type === 'overfunded' ? ['keep_full', 'partial_refund'] : []))
    : [];
  const previewsReady = requiredPreviewActions.length === 0
    || requiredPreviewActions.every((action) => feePreviews?.[action]);
  const previewsAvailable = previewsReady && !feePreviewLoading && !feePreviewError;

  const acceptPreview = transaction.discrepancy_type === 'underfunded'
    ? getPreviewFor('accept_partial')
    : previewPlaceholder;
  const denyPreview = transaction.discrepancy_type === 'underfunded'
    ? getPreviewFor('deny')
    : previewPlaceholder;
  const keepFullPreview = transaction.discrepancy_type === 'overfunded'
    ? getPreviewFor('keep_full')
    : previewPlaceholder;
  const refundOveragePreview = transaction.discrepancy_type === 'overfunded'
    ? getPreviewFor('partial_refund')
    : previewPlaceholder;
  const denyRefundDueDisplay = transaction.status === 'needs_resolution'
    ? (denyPreview.refundDue && denyPreview.refundDue !== '—'
      ? denyPreview.refundDue
      : receivedUsd.toFixed(2))
    : (refundDueUsd > 0 ? refundDueUsd.toFixed(2) : receivedUsd.toFixed(2));

  // Revenue split - use backend commission_data if available (from FICommissionPayout)
  // This uses actual ISO configuration, not hardcoded defaults
  const commissionData = transaction.commission_data;
  let isoCommission, solventFromFees, solventBuyRate, markup;

  if (commissionData) {
    // Use backend-calculated values (accurate to actual ISO config)
    isoCommission = parseFloat(commissionData.fi_revenue_usd || 0);
    solventFromFees = parseFloat(commissionData.solvent_revenue_usd || 0);
    solventBuyRate = parseFloat(commissionData.buy_rate_amount_usd || 0);
    markup = parseFloat(commissionData.spread_above_buy_rate_usd || 0);
  } else {
    // No commission snapshot for this session — never fabricate with default rates
    isoCommission = null;
    solventFromFees = null;
    solventBuyRate = null;
    markup = null;
  }

  // Solvent's total revenue = fees share + buffer profit
  const solventTotalRevenue = solventFromFees != null ? solventFromFees + bufferProfit : null;

  // Settlement variance (quoted vs received)
  const quotedCrypto = parseFloat(transaction.expected_crypto || 0);
  const receivedCrypto = parseFloat(transaction.received_crypto || 0);
  const cryptoVariance = receivedCrypto - quotedCrypto;
  const paymentVarianceUsd = parseFloat(transaction.payment_variance_usd || 0);
  const varianceVsPayment = parseOptionalFloat(transaction.rate_variance_usd)
    ?? (settlementUsd != null ? settlementUsd - paymentAmountUsd : null);
  const varianceVsQuote = parseOptionalFloat(transaction.variance_usd)
    ?? (settlementUsd != null && quotedTotalUsd != null ? settlementUsd - quotedTotalUsd : null);
  const quotedRate = parseOptionalFloat(transaction.quote_rate);
  const actualRate = parseOptionalFloat(transaction.received_rate);
  const quotedTotalDisplayUsd = quotedTotalUsd != null
    ? quotedTotalUsd
    : (quotedRate != null && quotedCrypto ? quotedCrypto * quotedRate : null);
  const actualTotalDisplayUsd = settlementUsd != null
    ? settlementUsd
    : (actualRate != null && receivedCrypto ? receivedCrypto * actualRate : null);
  // Awaiting settlement: payment detected on-chain but USD not confirmed yet
  // (before the Montgomery settlement webhook). Resolution is locked in this
  // state — the POS resolve endpoint requires settlement data. POS supplies
  // settlement_pending for every role; the crypto fallback covers
  // cached/legacy payloads that predate the flag.
  const awaitingSettlement = transaction.settlement_pending ?? (receivedCrypto > 0 && settlementUsd == null);
  // Best pre-settlement estimate of received value: crypto received at the
  // quote rate. Never fall back to the payment amount — that falsely reads
  // as an exact match when the customer under/over-paid.
  const estimatedReceivedUsd = actualTotalDisplayUsd != null
    ? actualTotalDisplayUsd
    : (quotedRate != null && receivedCrypto > 0 ? receivedCrypto * quotedRate : null);
  const isRefunded = transaction.status === 'refunded';
  const isDenied = transaction.resolution_action === 'deny';
  const refundDisplayUsd = refundedTotalUsd ?? receivedEstimateUsd ?? transactionAmount;

  const deniedRefundDue = isDenied && hasRefundDue ? refundDueUsd : 0;

  const settlementStatusLabel = isRefunded
    ? 'Refunded'
    : isDenied
      ? (hasRefundDue ? 'Denied - Refund Due' : 'Denied - Refunded')
      : settlementStatus === 'settled'
        ? 'Settled'
        : settlementStatus === 'failed'
          ? 'Failed'
          : 'Pending';
  const settlementStatusClass = isRefunded
    ? 'text-muted'
    : isDenied
      ? (hasRefundDue ? 'text-danger' : 'text-muted')
      : settlementStatus === 'settled'
        ? 'text-success'
        : settlementStatus === 'failed'
          ? 'text-danger'
          : 'text-warning';

  return (
    <div className={`drawer-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}>
      <div className="transaction-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header">
          <h2 className="drawer-title">Transaction Details</h2>
          <button className="drawer-close" onClick={onClose}>
            <Icons.X />
          </button>
        </div>

        <div className="drawer-body">
          {/* UNIFIED PAYMENT SUMMARY - Merchant View */}
          {isMerchantAdmin && (() => {
            const feeSummary = getFeeSummary(transaction);
            const netPayout = getNetPayout(transaction);
            const paymentAmount = parseOptionalFloat(transaction.expected_payment_usd)
              ?? transactionAmount;
            // New status model: 'needs_resolution' replaces old 'underfunded'/'overfunded' statuses
            // discrepancy_type field tracks what kind of discrepancy (underfunded/overfunded)
            const isPendingResolution = transaction.status === 'needs_resolution';
            const isRefundPending = transaction.status === 'refund_pending';
            // Paid with discrepancy (accepted partial or kept overpayment)
            const isPaidWithDiscrepancy = transaction.status === 'paid' && transaction.discrepancy_type && transaction.discrepancy_type !== 'none';
            const isDeniedStatus = isDenied;
            const acceptedAmount = parseOptionalFloat(transaction.accepted_amount_usd);
            const settledUsd = getSettlementUsd(transaction) ?? 0;
            const orderRef = transaction.external_reference || transaction.metadata?.order_id;

            // Status badge mapping (updated for new status model)
            const getStatusBadgeClass = (status, discrepancyType) => {
              // Use display_status_class if provided by backend
              if (transaction.display_status_class) {
                return `status-badge--${transaction.display_status_class}`;
              }
              const map = {
                paid: 'status-badge--paid',
                pending: 'status-badge--pending',
                needs_resolution: 'status-badge--needs-resolution',
                denied: 'status-badge--denied',
                refund_pending: 'status-badge--refund-pending',
                expired: 'status-badge--expired',
                refunded: 'status-badge--refunded',
                failed: 'status-badge--failed',
              };
              return map[status] || 'status-badge--pending';
            };

            return (
              <div className="payment-summary-unified">
                {/* Header: Order + Full Session ID + Status */}
                <div className="summary-header">
                  <div className="summary-header-left">
                    <div className="summary-order-ref">
                      {orderRef ? `Order #${orderRef}` : 'Payment Session'}
                    </div>
                    <div className="summary-session-id">{transaction.session_id || ''}</div>
                    {transaction.terminal_name && (
                      <div className="summary-session-id">Terminal: {transaction.terminal_name}</div>
                    )}
                  </div>
                  <span className={`status-badge ${getStatusBadgeClass(transaction.status)}`}>
                    {transaction.status_display || transaction.status?.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* ===== PENDING RESOLUTION (Underfunded/Overfunded) ===== */}
                {isPendingResolution && (
                  <>
                    {awaitingSettlement ? (
                      <div className="text-muted" style={{ marginBottom: '8px' }}>
                        ⏳ Crypto received — awaiting USD settlement. Accept/deny options
                        unlock once the settlement value is confirmed.
                      </div>
                    ) : (feePreviewLoading || feePreviewError) && (
                      <div
                        className={feePreviewError ? 'text-danger' : 'text-muted'}
                        style={{ marginBottom: '8px' }}
                      >
                        {feePreviewError ? 'Fee preview unavailable. Please refresh.' : 'Loading fee preview...'}
                      </div>
                    )}
                    {/* Discrepancy Box */}
                    {(() => {
                      const expectedUsd = paymentAmountUsd;
                      // Pre-settlement: estimate from crypto at quote rate, never 0
                      const receivedUsd = getSettlementUsd(transaction) ?? estimatedReceivedUsd ?? 0;
                      const discrepancyUsd = Math.abs(expectedUsd - receivedUsd);
                      const isUnderfunded = transaction.discrepancy_type === 'underfunded';

                      return (
                        <div className={`discrepancy-box ${!isUnderfunded ? 'discrepancy-box--overfunded' : ''}`}>
                          <div className="discrepancy-title">
                            {isUnderfunded ? 'Payment Shortfall' : 'Overpayment Received'}
                          </div>
                          <div className="discrepancy-grid">
                            <div className="discrepancy-item">
                              <div className="discrepancy-label">Expected</div>
                              <div className="discrepancy-value">${expectedUsd.toFixed(2)}</div>
                            </div>
                            <div className="discrepancy-item">
                              <div className="discrepancy-label">
                                {awaitingSettlement ? 'Received (est. at quote rate)' : 'Received'}
                              </div>
                              <div className="discrepancy-value">
                                {awaitingSettlement
                                  ? (estimatedReceivedUsd != null ? `~$${estimatedReceivedUsd.toFixed(2)}` : 'Pending settlement')
                                  : `$${receivedUsd.toFixed(2)}`}
                              </div>
                            </div>
                            <div className="discrepancy-item">
                              <div className="discrepancy-label">{isUnderfunded ? 'Shortfall' : 'Overage'}</div>
                              <div className={`discrepancy-value ${isUnderfunded ? 'text-danger' : 'text-success'}`}>
                                {isUnderfunded ? '-' : '+'}${discrepancyUsd.toFixed(2)}
                              </div>
                            </div>
                            <div className="discrepancy-item">
                              <div className="discrepancy-label">% of Expected</div>
                              <div className="discrepancy-value">
                                {expectedUsd > 0 ? ((receivedUsd / expectedUsd) * 100).toFixed(1) : '0.0'}%
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Fee Preview Sections — hidden while awaiting settlement
                        (previews would show "Received $0.00" without settlement data) */}
                    {!awaitingSettlement && previewsAvailable && (transaction.discrepancy_type === 'underfunded' ? (
                      <>
                        <div className="fee-preview-box">
                          <div className="fee-preview-title">If You Accept</div>
                          <div className="fee-preview-row">
                            <span>Received Amount</span>
                            <span>${(getSettlementUsd(transaction) ?? 0).toFixed(2)}</span>
                          </div>
                          <div className="fee-preview-row">
                            <span>Fees (recalculated)</span>
                            <span className="text-danger">-${acceptPreview.totalFees}</span>
                          </div>
                          <div className="fee-preview-row fee-preview-net">
                            <span>Net Payout</span>
                            <span className={getSignedAmountClass(acceptPreview.netPayout)}>${acceptPreview.netPayout}</span>
                          </div>
                        </div>
                        <div className="fee-preview-box fee-preview-box--secondary">
                          <div className="fee-preview-title">If You Deny</div>
                          <div className="fee-preview-row">
                          <span>Platform Non-Refundable</span>
                            <span className="text-danger">-${denyPreview.hardCosts}</span>
                          </div>
                          <div className="fee-preview-row fee-preview-net">
                            <span>Refund Due</span>
                            <span className="text-danger">-${denyRefundDueDisplay}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Option A: Keep Full */}
                        <div className="fee-preview-box">
                          <div className="fee-preview-title">Option A: Keep Full Amount</div>
                          <div className="fee-preview-row">
                            <span>Full Received</span>
                            <span>${(getSettlementUsd(transaction) ?? 0).toFixed(2)}</span>
                          </div>
                          <div className="fee-preview-row">
                            <span>Fees (on ${(getSettlementUsd(transaction) ?? 0).toFixed(2)})</span>
                            <span className="text-danger">-${keepFullPreview.totalFees}</span>
                          </div>
                          <div className="fee-preview-row fee-preview-net">
                            <span>Net Payout</span>
                            <span className={getSignedAmountClass(keepFullPreview.netPayout)}>${keepFullPreview.netPayout}</span>
                          </div>
                        </div>
                        {/* Option B: Refund Overage */}
                        <div className="fee-preview-box fee-preview-box--secondary">
                          <div className="fee-preview-title">Option B: Refund Overage</div>
                          <div className="fee-preview-row">
                            <span>Keep Amount</span>
                            <span>${expectedAmountUsd.toFixed(2)}</span>
                          </div>
                          <div className="fee-preview-row">
                            <span>Fees (on ${expectedAmountUsd.toFixed(2)})</span>
                            <span className="text-danger">-${refundOveragePreview.totalFees}</span>
                          </div>
                          <div className="fee-preview-row">
                            <span>Refund to Customer</span>
                            <span className="text-danger">-${refundOverageActual.toFixed(2)}</span>
                          </div>
                          <div className="fee-preview-row fee-preview-net">
                            <span>Net Payout</span>
                            <span className={getSignedAmountClass(refundOveragePreview.netPayout)}>${refundOveragePreview.netPayout}</span>
                          </div>
                        </div>
                      </>
                    ))}

                    {/* Resolution Actions — locked until USD settlement exists
                        (the resolve endpoint rejects pre-settlement requests) */}
                    {canResolve && !awaitingSettlement && (
                      <div className="summary-actions">
                        {resolutionSuccess ? (
                          <div className="resolution-success">✓ Resolution saved successfully. Refreshing...</div>
                        ) : (
                          <>
                            {transaction.discrepancy_type === 'underfunded' && (
                              <>
                                <button
                                  className="btn-summary btn-summary--accept"
                                  onClick={() => handleResolve('accept_partial', getSettlementUsd(transaction) ?? paymentAmountUsd)}
                                  disabled={resolving || !previewsAvailable}
                                >
                                  {resolving ? 'Processing...' : `Accept - Net $${acceptPreview.netPayout}`}
                                </button>
                                <button
                                  className="btn-summary btn-summary--deny"
                                  onClick={() => handleResolve('deny')}
                                  disabled={resolving || !previewsAvailable}
                                >
                                  {resolving ? 'Processing...' : 'Deny Payment'}
                                </button>
                              </>
                            )}
                            {transaction.discrepancy_type === 'overfunded' && (
                              <>
                                <button
                                  className="btn-summary btn-summary--accept"
                                  onClick={() => handleResolve('keep_full')}
                                  disabled={resolving || !previewsAvailable}
                                >
                                  {resolving ? 'Processing...' : `Keep Full - $${keepFullPreview.netPayout}`}
                                </button>
                                <button
                                  className="btn-summary btn-summary--secondary"
                                  onClick={() => handleResolve('partial_refund', expectedAmountUsd, refundOverageActual)}
                                  disabled={resolving || !previewsAvailable}
                                >
                                  {resolving ? 'Processing...' : 'Refund Overage'}
                                </button>
                              </>
                            )}
                            {resolutionError && <div className="resolution-error">{resolutionError}</div>}
                          </>
                        )}
                      </div>
                    )}

                    <div className="summary-note">
                      {transaction.discrepancy_type === 'underfunded'
                        ? `Processing fees apply to whatever amount you accept. If denied, you must refund $${denyRefundDueDisplay} to the customer.`
                        : 'Choose to keep the full overpayment or refund the excess to the customer.'
                      }
                    </div>

                    {/* Footer */}
                    <div className="summary-footer">
                      {transaction.received_at && <span>Paid: {moment(transaction.received_at).format('MMM D, YYYY h:mm A')}</span>}
                    </div>
                  </>
                )}

                {/* ===== DENIED - REFUND DUE ===== */}
                {isDeniedStatus && hasRefundDue && (
                  <>
                    <div className="summary-content">
                      <div className="amount-row">
                        <span className="amount-label">Original Sale</span>
                        <span className="amount-value text-muted">${transactionAmount.toFixed(2)}</span>
                      </div>
                      <div className="amount-row">
                        <span className="amount-label">Amount Received</span>
                        <span className="amount-value">${settledUsd.toFixed(2)}</span>
                      </div>
                      {/* For denied payments: show hard costs only (exchange + bank fees) */}
                      {feeSummary.hardCosts > 0 && (
                        <div className="amount-row">
                          <span className="amount-label">Platform Non-Refundable</span>
                          <span className="amount-value text-danger">-${feeSummary.hardCosts.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="amount-row">
                        <span className="amount-label">Decision</span>
                        <span className="amount-value text-danger">Payment Denied</span>
                      </div>
                      <div className="amount-row amount-row--total">
                        <span className="amount-label">Your Payout</span>
                        <span className={`amount-value ${getSignedAmountClass(netPayout)}`}>
                          ${netPayout !== null ? netPayout.toFixed(2) : '0.00'}
                        </span>
                      </div>
                    </div>

                    {/* Refund Due Card */}
                    <div className="refund-due-card">
                      <div className="refund-due-header">Refund Due to Customer</div>
                      <div className="refund-due-amount">${deniedRefundDue.toFixed(2)}</div>
                      {resolutionSuccess ? (
                        <div className="resolution-success">✓ Refund recorded successfully. Refreshing...</div>
                      ) : canMarkRefund ? (
                        <>
                          <div className="refund-form-group">
                            <select
                              value={refundMethod}
                              onChange={(e) => setRefundMethod(e.target.value)}
                              className="refund-select"
                            >
                              <option value="">Select refund method...</option>
                              {REFUND_METHOD_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="refund-form-group">
                            <input
                              type="text"
                              value={refundReference}
                              onChange={(e) => setRefundReference(e.target.value)}
                              placeholder="Reference # (optional)"
                              className="refund-input"
                            />
                          </div>
                          <div className="refund-form-group">
                            <textarea
                              value={refundNotes}
                              onChange={(e) => setRefundNotes(e.target.value)}
                              placeholder="Notes (optional)"
                              className="refund-textarea"
                              rows={2}
                            />
                          </div>
                          {refundError && <div className="refund-error">{refundError}</div>}
                          <button
                            className="btn-summary btn-summary--primary"
                            onClick={async () => {
                              if (!refundMethod) {
                                setRefundError('Please select a refund method');
                                return;
                              }
                              setRefundSubmitting(true);
                              setRefundError(null);
                              try {
                                await markPaymentRefunded(transaction.session_id, {
                                  refund_amount_usd: deniedRefundDue,
                                  refund_method: refundMethod,
                                  notes: refundNotes,
                                  reference_number: refundReference,
                                }, token);
                                // Show success and refresh after brief delay
                                setResolutionSuccess(true);
                                if (onResolve) {
                                  setTimeout(() => {
                                    onResolve();
                                    onClose();
                                  }, 1500);
                                }
                              } catch (err) {
                                setRefundError(err.message || 'Failed to record refund');
                              } finally {
                                setRefundSubmitting(false);
                              }
                            }}
                            disabled={refundSubmitting}
                          >
                            {refundSubmitting ? 'Processing...' : 'Mark as Refunded'}
                          </button>
                        </>
                      ) : (
                        <div className="refund-note">Contact your administrator to record this refund.</div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="summary-footer">
                      {transaction.received_at && <span>Paid: {moment(transaction.received_at).format('MMM D, YYYY h:mm A')}</span>}
                      {transaction.resolved_at && <span>Denied: {moment(transaction.resolved_at).format('MMM D, YYYY h:mm A')}</span>}
                    </div>
                  </>
                )}

                {/* ===== DENIED - REFUND COMPLETED ===== */}
                {isDeniedStatus && !hasRefundDue && (isRefunded || refundedTotalUsdSafe > 0) && (
                  <>
                    <div className="summary-content">
                      <div className="amount-row">
                        <span className="amount-label">Original Sale</span>
                        <span className="amount-value text-muted">${transactionAmount.toFixed(2)}</span>
                      </div>
                      <div className="amount-row">
                        <span className="amount-label">Amount Received</span>
                        <span className="amount-value">${settledUsd.toFixed(2)}</span>
                      </div>
                      {/* For denied payments: show hard costs only (exchange + bank fees) */}
                      {feeSummary.hardCosts > 0 && (
                        <div className="amount-row">
                          <span className="amount-label">Platform Non-Refundable</span>
                          <span className="amount-value text-danger">-${feeSummary.hardCosts.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="amount-row">
                        <span className="amount-label">Amount Refunded</span>
                        <span className="amount-value text-danger">-${refundedTotalUsdSafe.toFixed(2)}</span>
                      </div>
                      <div className="amount-row amount-row--total">
                        <span className="amount-label">Your Payout</span>
                        <span className={`amount-value ${getSignedAmountClass(netPayout)}`}>
                          ${netPayout !== null ? netPayout.toFixed(2) : '0.00'}
                        </span>
                      </div>
                    </div>

                    {/* Refund Completed Card */}
                    <div className="refund-due-card refund-due-card--completed">
                      <div className="refund-due-header">Refund Completed</div>
                      <div className="refund-due-amount">${refundedTotalUsdSafe.toFixed(2)}</div>
                      {denialRefundRecord?.refund_method && (
                        <div className="refund-method-info">
                          via {{
                            check: 'Check',
                            store_credit: 'Store Credit',
                            paypal: 'PayPal',
                            ach: 'ACH',
                            other: 'Other',
                          }[denialRefundRecord.refund_method] || denialRefundRecord.refund_method}
                          {denialRefundRecord.reference_number && ` - Ref: ${denialRefundRecord.reference_number}`}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="summary-footer">
                      {transaction.received_at && <span>Paid: {moment(transaction.received_at).format('MMM D, YYYY h:mm A')}</span>}
                      {denialRefundRecord?.created_at && <span>Refunded: {moment(denialRefundRecord.created_at).format('MMM D, YYYY h:mm A')}</span>}
                    </div>
                  </>
                )}

                {/* ===== REFUNDED (Full refund) ===== */}
                {isRefunded && !isDeniedStatus && (
                  <>
                    <div className="summary-content">
                      <div className="amount-row">
                        <span className="amount-label">Original Sale</span>
                        <span className="amount-value text-muted">${transactionAmount.toFixed(2)}</span>
                      </div>
                      <div className="amount-row">
                        <span className="amount-label">Amount Received</span>
                        <span className="amount-value">${settledUsd.toFixed(2)}</span>
                      </div>
                      {/* Show fees: hard costs for denied, total fees for accepted */}
                      {(feeSummary.isHardCostsOnly ? feeSummary.hardCosts : feeSummary.totalFees) > 0 && (
                        <div className="amount-row">
                          <span className="amount-label">
                            {feeSummary.isHardCostsOnly ? 'Platform Non-Refundable' : 'Processing Fees'}
                          </span>
                          <span className="amount-value text-danger">
                            -${(feeSummary.isHardCostsOnly ? feeSummary.hardCosts : feeSummary.totalFees).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="amount-row">
                        <span className="amount-label">Amount Refunded</span>
                        <span className="amount-value text-danger">-${refundDisplayUsd.toFixed(2)}</span>
                      </div>
                      <div className="amount-row amount-row--total">
                        <span className="amount-label">Your Payout</span>
                        <span className={`amount-value ${getSignedAmountClass(netPayout)}`}>
                          ${netPayout !== null ? netPayout.toFixed(2) : '0.00'}
                        </span>
                      </div>
                    </div>

                    {transaction.refunds?.[0] && (
                      <div className="refund-due-card refund-due-card--completed">
                        <div className="refund-due-header">Refund Completed</div>
                        <div className="refund-due-amount">${refundDisplayUsd.toFixed(2)}</div>
                        {transaction.refunds[0].refund_method && (
                          <div className="refund-method-info">
                            via {{
                              check: 'Check',
                              store_credit: 'Store Credit',
                              paypal: 'PayPal',
                              ach: 'ACH',
                              other: 'Other',
                            }[transaction.refunds[0].refund_method] || transaction.refunds[0].refund_method}
                            {transaction.refunds[0].reference_number && ` - Ref: ${transaction.refunds[0].reference_number}`}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="summary-footer">
                      {transaction.received_at && <span>Paid: {moment(transaction.received_at).format('MMM D, YYYY h:mm A')}</span>}
                      {transaction.refunds?.[0]?.created_at && <span>Refunded: {moment(transaction.refunds[0].created_at).format('MMM D, YYYY h:mm A')}</span>}
                    </div>
                  </>
                )}

                {/* ===== RESOLVED (Paid with Discrepancy - accepted partial, kept overpayment, or refund pending) =====
                    Denied sessions are excluded: their summary lives in the DENIED blocks above. */}
                {(isPaidWithDiscrepancy || isRefundPending) && !isDeniedStatus && (
                  <>
                    <div className="summary-content">
                      {/* Order context: what was expected (matches WooCommerce breakdown) */}
                      <div className="amount-row">
                        <span className="amount-label">Order Amount</span>
                        <span className="amount-value text-muted">${transactionAmount.toFixed(2)}</span>
                      </div>
                      {/* Show customer fee added if expected > order (fee added to order) */}
                      {expectedAmountUsd > transactionAmount + 0.01 && (
                        <div className="amount-row">
                          <span className="amount-label">Customer Fee</span>
                          <span className="amount-value text-muted">+${(expectedAmountUsd - transactionAmount).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Show expected payment total when customer fee was added */}
                      {expectedAmountUsd > transactionAmount + 0.01 && (
                        <div className="amount-row">
                          <span className="amount-label">Expected Payment</span>
                          <span className="amount-value text-muted">${expectedAmountUsd.toFixed(2)}</span>
                        </div>
                      )}
                      {/* What actually arrived */}
                      <div className="amount-row">
                        <span className="amount-label">Amount Received</span>
                        <span className="amount-value">${settledUsd.toFixed(2)}</span>
                      </div>
                      {/* Shortfall: compare received vs expected payment (not vs order amount) */}
                      {settledUsd < expectedAmountUsd - 0.01 && (
                        <div className="amount-row">
                          <span className="amount-label">Shortfall</span>
                          <span className="amount-value text-danger">-${(expectedAmountUsd - settledUsd).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Overage: for overfunded payments (keep_full or partial_refund) */}
                      {settledUsd > expectedAmountUsd + 0.01 && (transaction.resolution_action === 'partial_refund' || transaction.resolution_action === 'keep_full') && (
                        <div className="amount-row">
                          <span className="amount-label">Overage</span>
                          <span className="amount-value text-success">+${(settledUsd - expectedAmountUsd).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Settlement breakdown - matches WooCommerce */}
                      <div className="amount-row amount-row--separator">
                        <span className="amount-label">Accepted Amount</span>
                        <span className="amount-value">${(acceptedAmount ?? settledUsd).toFixed(2)}</span>
                      </div>
                      {/* Fee breakdown */}
                      {transaction.resolution_action === 'deny' ? (
                        totalFees > AMOUNT_DISPLAY_THRESHOLD && (
                          <div className="amount-row">
                            <span className="amount-label">Platform Non-Refundable</span>
                            <span className="amount-value text-danger">-${totalFees.toFixed(2)}</span>
                          </div>
                        )
                      ) : (
                        <>
                          {merchantFee > AMOUNT_DISPLAY_THRESHOLD && (
                            <div className="amount-row">
                              <span className="amount-label">Merchant Fee</span>
                              <span className="amount-value text-danger">-${merchantFee.toFixed(2)}</span>
                            </div>
                          )}
                          {customerFee > AMOUNT_DISPLAY_THRESHOLD && (
                            <div className="amount-row">
                              <span className="amount-label">Customer Fee</span>
                              <span className="amount-value text-danger">-${customerFee.toFixed(2)}</span>
                            </div>
                          )}
                        </>
                      )}
                      {/* Show refunds deduction if any manual refunds exist */}
                      {manualRefundedTotal > AMOUNT_DISPLAY_THRESHOLD && (
                        <div className="amount-row">
                          <span className="amount-label">Refunds Issued</span>
                          <span className="amount-value text-danger">-${manualRefundedTotal.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="amount-row amount-row--total">
                        <span className="amount-label">Net Payout</span>
                        <span className={`amount-value ${transaction.resolution_action === 'deny' ? 'text-muted' : getSignedAmountClass(netPayout)}`}>
                          {transaction.resolution_action === 'deny'
                            ? '$0.00'
                            : (netPayout !== null ? `$${netPayout.toFixed(2)}` : 'Pending')}
                        </span>
                      </div>
                      {transaction.resolution_action === 'deny' && (
                        <div className="amount-row">
                          <span className="amount-label">Refund Due</span>
                          <span className="amount-value text-danger">-${denyRefundDueDisplay}</span>
                        </div>
                      )}
                    </div>

                    {/* Resolution Info - show if there was a resolution action OR auto-accepted discrepancy */}
                    {(transaction.resolution_action || (transaction.status === 'paid' && transaction.discrepancy_type && transaction.discrepancy_type !== 'none')) && (
                      <div className="resolution-info-card">
                        <div className="resolution-info-title">Resolution</div>
                        <div className="resolution-info-row">
                          <span>Action:</span>
                          <span>{{
                            accept_partial: 'Accepted partial payment',
                            keep_full: 'Kept full overpayment',
                            partial_refund: 'Refund overage',
                            deny: 'Denied payment',
                          }[transaction.resolution_action] || (
                            // Auto-accepted payments without explicit resolution action
                            transaction.status === 'paid' && transaction.discrepancy_type === 'underfunded'
                              ? 'Auto-accepted (within tolerance)'
                              : transaction.status === 'paid' && transaction.discrepancy_type === 'overfunded'
                                ? 'Auto-accepted (within threshold)'
                                : transaction.resolution_action
                          )}</span>
                        </div>
                        {transaction.resolved_at && (
                          <div className="resolution-info-row">
                            <span>Resolved:</span>
                            <span>{moment(transaction.resolved_at).format('MMM D, YYYY h:mm A')}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Overage Refund Due (for partial_refund resolution) */}
                    {transaction.resolution_action === 'partial_refund' && (() => {
                      const hasPendingOverageRefund = refundDueSource === 'overage' && hasRefundDue;
                      const overageRefundAmount = hasPendingOverageRefund
                        ? refundDueUsd
                        : (parseOptionalFloat(overageRefundRecord?.refund_amount_usd) ?? 0);

                      if (overageRefundAmount > AMOUNT_DISPLAY_THRESHOLD) {
                        const isOverageRefunded = !hasPendingOverageRefund;

                        return (
                          <div className={`refund-due-card ${isOverageRefunded ? 'refund-due-card--completed' : ''}`}>
                            <div className="refund-due-header">{isOverageRefunded ? 'Overage Refund Completed' : 'Overage Refund Due'}</div>
                            <div className="refund-due-amount">${parseFloat(overageRefundAmount).toFixed(2)}</div>
                            {!isOverageRefunded && canMarkRefund && (
                              <button
                                className="btn-summary btn-summary--primary"
                                onClick={openResolutionRefundModal}
                              >
                                Record Overage Refund
                              </button>
                            )}
                            {isOverageRefunded && overageRefundRecord?.refund_method && (
                              <>
                                <div className="refund-method-info">
                                  via {{
                                    check: 'Check',
                                    store_credit: 'Store Credit',
                                    paypal: 'PayPal',
                                    ach: 'ACH',
                                    other: 'Other',
                                  }[overageRefundRecord.refund_method] || overageRefundRecord.refund_method}
                                  {overageRefundRecord.reference_number && ` - Ref: ${overageRefundRecord.reference_number}`}
                                </div>
                                {overageRefundRecord.refund_notes && (
                                  <div className="refund-notes-info">
                                    Notes: {overageRefundRecord.refund_notes}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* General Refund Action (for accepted amount, separate from overage) */}
                    {canShowRefundButton && transaction.resolution_action !== 'partial_refund' && (
                      <div className="refund-action-section">
                        <button
                          className="btn-summary btn-summary--outline"
                          onClick={openRefundModal}
                        >
                          Issue Refund
                        </button>
                      </div>
                    )}

                    {/* For partial_refund: show refund button only if overage is already refunded */}
                    {canShowRefundButton && transaction.resolution_action === 'partial_refund' && !hasRefundDue && remainingRefundable > AMOUNT_DISPLAY_THRESHOLD && (
                      <div className="refund-action-section">
                        <button
                          className="btn-summary btn-summary--outline"
                          onClick={openRefundModal}
                        >
                          Issue Additional Refund
                        </button>
                      </div>
                    )}

                    {/* Fully Refunded indicator - shows when all refundable amount has been refunded */}
                    {remainingRefundable <= AMOUNT_DISPLAY_THRESHOLD && refundedTotalUsdSafe > AMOUNT_DISPLAY_THRESHOLD && !hasRefundDue && (
                      <div className="refund-due-card refund-due-card--completed">
                        <div className="refund-due-header">Fully Refunded</div>
                        <div className="refund-due-amount">${refundedTotalUsdSafe.toFixed(2)}</div>
                        <div
                          className="refund-method-info"
                          style={{ color: netPayout < -AMOUNT_DISPLAY_THRESHOLD ? '#b91c1c' : '#166534', marginTop: '4px' }}
                        >
                          Net Payout: ${netPayout !== null ? netPayout.toFixed(2) : '0.00'}
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="summary-footer">
                      {transaction.received_at && <span>Paid: {moment(transaction.received_at).format('MMM D, YYYY h:mm A')}</span>}
                    </div>
                  </>
                )}

                {/* ===== NORMAL PAID ===== */}
                {transaction.status === 'paid' && !isPendingResolution && !isDeniedStatus && !isRefunded && !isPaidWithDiscrepancy && !isRefundPending && (
                  <>
                    <div className="summary-content">
                      {/* Sale Amount */}
                      <div className="amount-row">
                        <span className="amount-label">Sale Amount</span>
                        <span className="amount-value">${transactionAmount.toFixed(2)}</span>
                      </div>
                      {/* Show customer fee added if expected > sale (fee added to order) */}
                      {expectedAmountUsd > transactionAmount + 0.01 && (
                        <div className="amount-row">
                          <span className="amount-label">Customer Fee</span>
                          <span className="amount-value text-muted">+${(expectedAmountUsd - transactionAmount).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Show expected payment total when customer fee was added */}
                      {expectedAmountUsd > transactionAmount + 0.01 && (
                        <div className="amount-row">
                          <span className="amount-label">Expected Payment</span>
                          <span className="amount-value text-muted">${expectedAmountUsd.toFixed(2)}</span>
                        </div>
                      )}
                      {/* Amount Received - always show when settled */}
                      {/* For buffer profit (crypto matched, settlement higher), show expected not inflated settlement */}
                      {/* Buffer profit goes to Solvent, merchant gets credit for expected payment only */}
                      {settlementStatus === 'settled' && settledUsd > 0 && (() => {
                        const isBufferProfit = transaction.discrepancy_type !== 'overfunded' && settledUsd > expectedAmountUsd + 0.01;
                        const displayAmount = isBufferProfit ? expectedAmountUsd : settledUsd;
                        return (
                          <div className="amount-row">
                            <span className="amount-label">Amount Received</span>
                            <span className="amount-value">${displayAmount.toFixed(2)}</span>
                          </div>
                        );
                      })()}
                      {/* Shortfall - show when received less than expected (rate slippage) */}
                      {/* Merchant absorbs rate loss when crypto matches but settlement is lower */}
                      {settledUsd < expectedAmountUsd - 0.01 && (
                        <div className="amount-row">
                          <span className="amount-label">Shortfall</span>
                          <span className="amount-value text-danger">-${(expectedAmountUsd - settledUsd).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Overage - ONLY show for crypto overpayment (discrepancy_type=overfunded) */}
                      {/* Buffer profit (crypto matched, settlement higher) goes to Solvent, not merchant */}
                      {transaction.discrepancy_type === 'overfunded' && settledUsd > expectedAmountUsd + 0.01 && (
                        <div className="amount-row">
                          <span className="amount-label">Overage</span>
                          <span className="amount-value text-success">+${(settledUsd - expectedAmountUsd).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Processing Fees - show for all fee payer modes */}
                      {feeSummary.feePayer === 'merchant' && feeSummary.merchantFee > 0 && (
                        <div className="amount-row">
                          <span className="amount-label">Processing Fee</span>
                          <span className="amount-value text-danger">-${feeSummary.merchantFee.toFixed(2)}</span>
                        </div>
                      )}
                      {feeSummary.feePayer === 'customer' && feeSummary.customerFee > 0 && (
                        <div className="amount-row">
                          <span className="amount-label">Customer Fee</span>
                          <span className="amount-value text-danger">-${feeSummary.customerFee.toFixed(2)}</span>
                        </div>
                      )}
                      {feeSummary.feePayer === 'shared' && (
                        <>
                          {feeSummary.merchantFee > 0 && (
                            <div className="amount-row">
                              <span className="amount-label">Merchant Fee</span>
                              <span className="amount-value text-danger">-${feeSummary.merchantFee.toFixed(2)}</span>
                            </div>
                          )}
                          {feeSummary.customerFee > 0 && (
                            <div className="amount-row">
                              <span className="amount-label">Customer Fee</span>
                              <span className="amount-value text-danger">-${feeSummary.customerFee.toFixed(2)}</span>
                            </div>
                          )}
                        </>
                      )}
                      {/* Your Payout */}
                      <div className="amount-row amount-row--total">
                        <span className="amount-label">Your Payout</span>
                        <span className={`amount-value ${settlementStatus === 'settled' ? getSignedAmountClass(netPayout) : 'text-warning'}`}>
                          {settlementStatus === 'settled'
                            ? `$${netPayout !== null ? netPayout.toFixed(2) : '0.00'}`
                            : 'Pending settlement'
                          }
                        </span>
                      </div>
                    </div>

                    {/* Resolution Info - show if this was an overfunded payment resolved with keep_full */}
                    {transaction.resolution_action === 'keep_full' && (
                      <div className="resolution-info-card">
                        <div className="resolution-info-title">Resolution</div>
                        <div className="resolution-info-row">
                          <span>Action:</span>
                          <span>Kept full overpayment</span>
                        </div>
                        {transaction.resolved_at && (
                          <div className="resolution-info-row">
                            <span>Resolved:</span>
                            <span>{moment(transaction.resolved_at).format('MMM D, YYYY h:mm A')}</span>
                          </div>
                        )}
                      </div>
                    )}


                    {/* Refund Action */}
                    {canShowRefundButton && (
                      <div className="refund-action-section">
                        <button
                          className="btn-summary btn-summary--outline"
                          onClick={openRefundModal}
                        >
                          Issue Refund
                        </button>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="summary-footer">
                      {transaction.received_at && <span>Paid: {moment(transaction.received_at).format('MMM D, YYYY h:mm A')}</span>}
                    </div>
                  </>
                )}

                {/* ===== NO PAYMENT (expired / failed / pending) ===== */}
                {['expired', 'failed', 'pending'].includes(transaction.status) && (
                  <div className="summary-content">
                    <div className="amount-row">
                      <span className="amount-label">Sale Amount</span>
                      <span className="amount-value">${transactionAmount.toFixed(2)}</span>
                    </div>
                    <div className="summary-note">
                      {transaction.status === 'pending'
                        ? 'Awaiting customer payment.'
                        : 'No payment was received for this session.'}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Payment Info - Admin/SuperAdmin View */}
          {!isMerchantAdmin && (
            <div className="drawer-section">
              <div className="drawer-section-title">Payment Session</div>
              <div className="detail-grid">
                <span className="detail-label">Session ID</span>
                <span className="detail-value mono">{transaction.session_id}</span>

                {(transaction.external_reference || transaction.metadata?.order_id) && (
                  <>
                    <span className="detail-label">WooCommerce Order</span>
                    <span className="detail-value mono">#{transaction.external_reference || transaction.metadata?.order_id}</span>
                  </>
                )}

                <span className="detail-label">Status</span>
                <span className="detail-value">
                  <span className={`status-badge status-badge--${transaction.status}`}>
                    {transaction.status_display || transaction.status}
                  </span>
                </span>

                <span className="detail-label">Amount (USD)</span>
                <span className="detail-value amount">${detailAmountUsd.toFixed(2)}</span>

                {isSuperAdmin && quotedCrypto > 0 && (
                  <>
                    <span className="detail-label">Quoted ({transaction.crypto_currency || 'Crypto'})</span>
                    <span className="detail-value mono">
                      {quotedCrypto.toFixed(getCryptoDecimals(transaction.crypto_currency))} {transaction.crypto_currency || ''}
                    </span>
                  </>
                )}

                <span className="detail-label">Merchant Fee</span>
                <span className="detail-value">${merchantFee.toFixed(2)}</span>

                {customerFee > 0 && (
                  <>
                    <span className="detail-label">Customer Fee</span>
                    <span className="detail-value">${customerFee.toFixed(2)}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Payment Amounts Breakdown (SuperAdmin/SolventAdmin only - shows for any status with payment data) */}
          {isSuperAdmin && (quotedCrypto > 0 || receivedCrypto > 0) && (
            <div className={`drawer-section ${transaction.status === 'needs_resolution' ? 'drawer-section--highlight' : ''}`}>
              <div className="drawer-section-title">
                {transaction.status === 'needs_resolution' ? '⚠️ Payment Breakdown' : 'Payment Breakdown'}
              </div>

              {/* Crypto Comparison - Most important for understanding what happened */}
              <div className="comparison-card">
                <div className="comparison-header">{transaction.crypto_currency || 'Crypto'} (What Was Sent)</div>
                <div className="comparison-grid">
                  <div className="comparison-item">
                    <span className="comparison-label">Requested</span>
                    <span className="comparison-value mono">
                      {quotedCrypto.toFixed(getCryptoDecimals(transaction.crypto_currency))} {transaction.crypto_currency || ''}
                    </span>
                  </div>
                  <div className="comparison-item">
                    <span className="comparison-label">Received</span>
                    <span className="comparison-value mono">
                      {receivedCrypto > 0
                        ? `${receivedCrypto.toFixed(getCryptoDecimals(transaction.crypto_currency))} ${transaction.crypto_currency || ''}`
                        : 'Pending'
                      }
                    </span>
                  </div>
                  {receivedCrypto > 0 && (
                    <div className="comparison-item comparison-variance">
                      <span className="comparison-label">Crypto Difference</span>
                      <span className={`comparison-value mono ${cryptoMatched ? 'exact' : cryptoVariance > 0 ? 'positive' : 'negative'}`}>
                        {cryptoMatched
                          ? (cryptoVariance === 0 ? '✓ Exact match' : '✓ Within tolerance')
                          : `${cryptoVariance > 0 ? '+' : ''}${cryptoVariance.toFixed(getCryptoDecimals(transaction.crypto_currency))} ${transaction.crypto_currency || ''}`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* USD Comparison */}
              <div className="comparison-card" style={{ marginTop: '0.75rem' }}>
                <div className="comparison-header">USD (Settlement Value)</div>
                <div className="comparison-grid">
                  <div className="comparison-item">
                    <span className="comparison-label">Payment Amount</span>
                    <span className="comparison-value">${paymentAmountUsd.toFixed(2)}</span>
                  </div>
                  {isSuperAdmin && quotedTotalUsd != null && Math.abs(quotedTotalUsd - paymentAmountUsd) >= 0.01 && (
                    <div className="comparison-item">
                      <span className="comparison-label">Quoted Total (incl. buffer)</span>
                      <span className="comparison-value">${quotedTotalUsd.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="comparison-item">
                    <span className="comparison-label">
                      {settlementUsd != null ? 'Settled Amount' : 'Received Value'}
                    </span>
                    <span className="comparison-value">
                      {settlementUsd != null
                        ? `$${settlementUsd.toFixed(2)}`
                        : estimatedReceivedUsd != null
                          ? `~$${estimatedReceivedUsd.toFixed(2)} (est. at quote rate)`
                          : 'Pending'
                      }
                    </span>
                  </div>
                  {receivedCrypto > 0 && (
                    <div className="comparison-item comparison-variance">
                      <span className="comparison-label">Variance vs Payment</span>
                      {(() => {
                        // Settled: authoritative variance from the backend.
                        // Awaiting settlement: estimate from crypto received at
                        // the quote rate — marked (est.), never "exact match".
                        const usdDiff = settlementUsd != null
                          ? (varianceVsPayment ?? paymentVarianceUsd)
                          : (estimatedReceivedUsd != null ? estimatedReceivedUsd - paymentAmountUsd : null);
                        if (usdDiff == null) {
                          return <span className="comparison-value">Pending settlement</span>;
                        }
                        const cls = Math.abs(usdDiff) < 0.01 ? 'exact' : usdDiff > 0 ? 'positive' : 'negative';
                        const text = Math.abs(usdDiff) < 0.01
                          ? (settlementUsd != null ? '✓ Exact match' : '✓ Matches (est.)')
                          : `${settlementUsd == null ? '~' : ''}${usdDiff > 0 ? '+' : ''}$${usdDiff.toFixed(2)}${settlementUsd == null ? ' (est.)' : ''}`;
                        return <span className={`comparison-value ${cls}`}>{text}</span>;
                      })()}
                    </div>
                  )}
                  {isSuperAdmin && varianceVsQuote != null && quotedTotalUsd != null && Math.abs(quotedTotalUsd - paymentAmountUsd) >= 0.01 && (
                    <div className="comparison-item comparison-variance">
                      <span className="comparison-label">Variance vs Quote (incl. buffer)</span>
                      <span className={`comparison-value ${Math.abs(varianceVsQuote) < 0.01 ? 'exact' : varianceVsQuote > 0 ? 'positive' : 'negative'}`}>
                        {Math.abs(varianceVsQuote) < 0.01
                          ? '✓ Exact match'
                          : `${varianceVsQuote > 0 ? '+' : ''}$${varianceVsQuote.toFixed(2)}`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Explanation for discrepancy statuses */}
              {transaction.status === 'needs_resolution' && cryptoVariance !== 0 && (
                <div className="comparison-explanation">
                  {transaction.discrepancy_type === 'underfunded'
                    ? `Customer sent ${Math.abs(cryptoVariance).toFixed(getCryptoDecimals(transaction.crypto_currency))} ${transaction.crypto_currency || ''} less than quoted. This is a crypto shortfall that needs resolution.`
                    : `Customer sent ${Math.abs(cryptoVariance).toFixed(getCryptoDecimals(transaction.crypto_currency))} ${transaction.crypto_currency || ''} more than quoted. This overpayment needs resolution.`
                  }
                </div>
              )}

              {/* Explanation when crypto matched but USD differs (volatility) */}
              {transaction.status === 'paid' && cryptoMatched && transaction.rate_variance_usd && Math.abs(transaction.rate_variance_usd) >= 0.01 && (
                <div className="comparison-explanation comparison-explanation--info">
                  Crypto amount {cryptoVariance === 0 ? 'matched exactly' : 'matched within tolerance'}. USD variance of {transaction.rate_variance_usd > 0 ? '+' : ''}${transaction.rate_variance_usd.toFixed(2)} vs payment is due to rate fluctuation at settlement.
                  {transaction.rate_variance_usd < 0 && ' Merchant receives the actual settled amount.'}
                </div>
              )}
            </div>
          )}

          {/* Settlement Details (SuperAdmin only - shows settlement data) */}
          {isSuperAdmin && settlementUsd != null && (
            <div className="drawer-section">
              <div className="drawer-section-title">Settlement Details</div>
              <div className="detail-grid">
                <span className="detail-label">Payment Amount</span>
                <span className="detail-value">
                  ${paymentAmountUsd.toFixed(2)}
                </span>

                {quotedRate != null && quotedCrypto > 0 && quotedTotalDisplayUsd != null && (
                  <>
                    <span className="detail-label">Quoted (incl. buffer)</span>
                    <span className="detail-value mono">
                      {quotedCrypto.toFixed(getCryptoDecimals(transaction.crypto_currency))} {transaction.crypto_currency || ''} @ ${quotedRate.toFixed(2)} = ${quotedTotalDisplayUsd.toFixed(2)}
                    </span>
                  </>
                )}

                {actualRate != null && receivedCrypto > 0 && actualTotalDisplayUsd != null && (
                  <>
                    <span className="detail-label">Actual</span>
                    <span className="detail-value mono">
                      {receivedCrypto.toFixed(getCryptoDecimals(transaction.crypto_currency))} {transaction.crypto_currency || ''} @ ${actualRate.toFixed(2)} = ${actualTotalDisplayUsd.toFixed(2)}
                    </span>
                  </>
                )}

                {transaction.rate_variance_pct != null && (
                  <>
                    <span className="detail-label">Rate Variance</span>
                    <span className={`detail-value ${transaction.rate_variance_pct >= 0 ? 'text-success' : 'text-danger'}`}>
                      {transaction.rate_variance_pct >= 0 ? '+' : ''}{parseFloat(transaction.rate_variance_pct).toFixed(4)}%
                    </span>
                  </>
                )}

                {transaction.rate_variance_usd != null && (
                  <>
                    <span className="detail-label">Variance vs Payment</span>
                    <span className={`detail-value ${transaction.rate_variance_usd >= 0 ? 'text-success' : 'text-danger'}`}>
                      {transaction.rate_variance_usd >= 0 ? '+' : ''}${parseFloat(transaction.rate_variance_usd).toFixed(2)}
                    </span>
                  </>
                )}

                {cryptoMatched && transaction.buffer_expected_usd != null && (
                  <>
                    <span className="detail-label">Buffer Expected</span>
                    <span className="detail-value">
                      ${parseFloat(transaction.buffer_expected_usd).toFixed(2)}
                    </span>
                  </>
                )}

                {cryptoMatched && transaction.buffer_realized_usd != null && (
                  <>
                    <span className="detail-label">Buffer Realized</span>
                    <span className="detail-value">
                      ${parseFloat(transaction.buffer_realized_usd).toFixed(2)}
                    </span>
                  </>
                )}

                {cryptoMatched && transaction.market_move_usd != null && (
                  <>
                    <span className="detail-label">Market Move</span>
                    <span className={`detail-value ${transaction.market_move_usd >= 0 ? 'text-success' : 'text-danger'}`}>
                      {transaction.market_move_usd >= 0 ? '+' : ''}${parseFloat(transaction.market_move_usd).toFixed(2)}
                    </span>
                  </>
                )}

                {transaction.received_at && (
                  <>
                    <span className="detail-label">Received At</span>
                    <span className="detail-value">
                      {moment(transaction.received_at).format('MMM D, YYYY h:mm A')}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Fee Rates Applied (SuperAdmin only) */}
          {isSuperAdmin && (transaction.merchant_fee_pct !== undefined || transaction.customer_fee_pct !== undefined) && (
            <div className="drawer-section">
              <div className="drawer-section-title">Fee Rates Applied</div>
              <div className="detail-grid">
                {transaction.merchant_fee_pct !== undefined && transaction.merchant_fee_pct !== null && (
                  <>
                    <span className="detail-label">Merchant Fee Rate</span>
                    <span className="detail-value">{parseFloat(transaction.merchant_fee_pct).toFixed(2)}%</span>
                  </>
                )}

                {transaction.customer_fee_pct !== undefined && transaction.customer_fee_pct !== null && (
                  <>
                    <span className="detail-label">Customer Fee Rate</span>
                    <span className="detail-value">{parseFloat(transaction.customer_fee_pct).toFixed(2)}%</span>
                  </>
                )}

                {transaction.fee_payer && (
                  <>
                    <span className="detail-label">Fee Payer</span>
                    <span className="detail-value" style={{ textTransform: 'capitalize' }}>{transaction.fee_payer}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Merchant Info - Not shown to merchant admins (they already know their own merchant) */}
          {!isMerchantAdmin && (
            <div className="drawer-section">
              <div className="drawer-section-title">Merchant</div>
              <div className="detail-grid">
                <span className="detail-label">Name</span>
                <span className="detail-value">{transaction.businessName || transaction.merchant_name || 'N/A'}</span>

                <span className="detail-label">Terminal</span>
                <span className="detail-value">{transaction.terminal_name || 'N/A'}</span>

                {isSuperAdmin && (
                  <>
                    <span className="detail-label">Merchant ID</span>
                    <span className="detail-value mono">{transaction.merchant || 'N/A'}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Revenue Split - Super Admin View */}
          {isSuperAdmin && (
            <div className="drawer-section">
              <div className="drawer-section-title">Revenue Split</div>

              {commissionData ? (
                <>
                  {/* Fee Split Card */}
                  <div className="commission-card">
                    <div className="commission-row">
                      <span className="commission-label">Total Fees Collected</span>
                      <span className="commission-value">${totalFees.toFixed(2)}</span>
                    </div>
                    <div className="commission-row">
                      <span className="commission-label">
                        {`Solvent Buy Rate (${parseFloat(commissionData.buy_rate_pct)}% + $${parseFloat(commissionData.buy_rate_usd).toFixed(2)})`}
                      </span>
                      <span className="commission-value">-${solventBuyRate.toFixed(2)}</span>
                    </div>
                    <div className="commission-row total">
                      <span className="commission-label">Markup (Split Pool)</span>
                      <span className="commission-value">${markup.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="commission-highlight">
                    <span className="commission-highlight-label">
                      {`ISO Commission (${parseFloat(commissionData.reseller_split_pct)}%)`}
                    </span>
                    <span className="commission-highlight-value">${isoCommission.toFixed(2)}</span>
                  </div>

                  <div className="commission-highlight commission-highlight--solvent">
                    <span className="commission-highlight-label">
                      {`Solvent from Fees (${100 - parseFloat(commissionData.reseller_split_pct)}% + Buy Rate)`}
                    </span>
                    <span className="commission-highlight-value">${solventFromFees.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="text-muted" style={{ fontSize: '13px', padding: '0.5rem 0' }}>
                  Commission breakdown unavailable for this session.
                </div>
              )}

              {/* Buffer Profit - 100% Solvent (crypto matched only) */}
              {cryptoMatched && (
                <div className="commission-highlight commission-highlight--buffer">
                  <span className="commission-highlight-label">
                    Buffer Realized (100% Solvent)
                    <span className="commission-tooltip" title="Buffer is applied to the crypto exchange rate. Realized buffer is scaled by the actual settlement rate.">
                      ?
                    </span>
                  </span>
                  <span className="commission-highlight-value">${bufferProfit.toFixed(2)}</span>
                </div>
              )}

              {/* Crypto mismatch impact (over/underpayment) */}
              {!cryptoMatched && cryptoMismatchUsd !== null && Math.abs(cryptoMismatchUsd) >= 0.01 && (
                <div className={`commission-highlight commission-highlight--market ${cryptoMismatchUsd < 0 ? 'negative' : ''}`}>
                  <span className="commission-highlight-label">
                    {cryptoMismatchUsd > 0 ? 'Crypto Overpayment (Mismatch)' : 'Crypto Shortfall (Mismatch)'}
                    <span className="commission-tooltip" title="Customer sent more/less crypto than quoted. This is not market movement or buffer; resolution determines the final outcome.">
                      ?
                    </span>
                  </span>
                  <span className="commission-highlight-value">
                    {cryptoMismatchUsd > 0 ? '+' : '-'}${Math.abs(cryptoMismatchUsd).toFixed(2)}
                  </span>
                </div>
              )}

              {/* Market Move (rate variance on payment amount) */}
              {Math.abs(marketMoveUsd) >= 0.01 && (
                <div className={`commission-highlight commission-highlight--market ${marketMoveUsd < 0 ? 'negative' : ''}`}>
                  <span className="commission-highlight-label">
                    Market Move (vs Quote)
                    <span className="commission-tooltip" title="Impact of settlement rate movement on the expected payment amount. Negative values reflect merchant shortfall when crypto matched.">
                      ?
                    </span>
                  </span>
                  <span className="commission-highlight-value">
                    {marketMoveUsd > 0 ? '+' : ''}${marketMoveUsd.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Hard Costs (Accounting) */}
              {(exchangeFee > 0 || bankFee > 0) && (
                <div className="commission-card" style={{ marginTop: '0.75rem', background: '#fef2f2' }}>
                  <div className="commission-row">
                    <span className="commission-label" style={{ color: '#991b1b' }}>Hard Costs (Accounting)</span>
                    <span className="commission-value"></span>
                  </div>
                  {exchangeFee > 0 && (
                    <div className="commission-row">
                      <span className="commission-label">Exchange Fee (25 bps)</span>
                      <span className="commission-value negative">-${exchangeFee.toFixed(4)}</span>
                    </div>
                  )}
                  {bankFee > 0 && (
                    <div className="commission-row">
                      <span className="commission-label">Bank Fee (25 bps)</span>
                      <span className="commission-value negative">-${bankFee.toFixed(4)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Solvent Total Revenue Breakdown */}
              {commissionData && (
                <div className="solvent-revenue-summary">
                  <div className="solvent-revenue-header">Solvent Total Revenue</div>
                  <div className="solvent-revenue-breakdown">
                    <div className="revenue-line">
                      <span>From Fee Split</span>
                      <span>${solventFromFees.toFixed(2)}</span>
                    </div>
                    {cryptoMatched && (
                      <div className="revenue-line buffer-line">
                        <span>From Buffer</span>
                        <span>${bufferProfit.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="revenue-line total-line">
                      <span>Total</span>
                      <span>${solventTotalRevenue.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* FI needs-resolution visibility: discrepancy amount + next-step context.
              discrepancy_amount_usd is computed server-side by TerraBE (expected - received)
              before crypto/received fields are stripped for FI roles. */}
          {isFIAdmin && !isSuperAdmin && transaction.status === 'needs_resolution' && (() => {
            const rawDiscrepancy = transaction.discrepancy_amount_usd != null
              ? parseFloat(transaction.discrepancy_amount_usd)
              : null;
            const discrepancyAbs = rawDiscrepancy != null && !Number.isNaN(rawDiscrepancy)
              ? Math.abs(rawDiscrepancy)
              : null;
            const isOverfunded = transaction.discrepancy_type === 'overfunded';
            const directionLabel = isOverfunded
              ? 'Overfunded'
              : transaction.discrepancy_type === 'underfunded'
                ? 'Underfunded'
                : 'Payment discrepancy';
            return (
              <div className="drawer-section resolution-section">
                <div className="drawer-section-title resolution-title">
                  {isOverfunded ? '💰 ' : '⚠️ '}
                  {discrepancyAbs != null
                    ? `${directionLabel} by $${discrepancyAbs.toFixed(2)} — awaiting merchant action`
                    : `${directionLabel} — awaiting merchant action`}
                </div>
                <p className="text-muted" style={{ margin: 0, fontSize: '13px' }}>
                  The merchant resolves this from their dashboard; you'll see the outcome here.
                </p>
              </div>
            );
          })()}

          {/* Commission - ISO Admin View (Simplified) */}
          {isFIAdmin && !isSuperAdmin && (
            <div className="drawer-section">
              <div className="drawer-section-title">Your Commission</div>
              {commissionData ? (
                <>
                  <div className="commission-card">
                    <div className="commission-row">
                      <span className="commission-label">Total Fees Collected</span>
                      <span className="commission-value">${totalFees.toFixed(2)}</span>
                    </div>
                    <div className="commission-row">
                      <span className="commission-label">Markup Pool</span>
                      <span className="commission-value">${markup.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="commission-highlight">
                    <span className="commission-highlight-label">
                      {`Your Share (${parseFloat(commissionData.reseller_split_pct)}%)`}
                    </span>
                    <span className="commission-highlight-value">${isoCommission.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="text-muted" style={{ fontSize: '13px', padding: '0.5rem 0' }}>
                  Commission breakdown unavailable for this session.
                </div>
              )}
            </div>
          )}

          {/* Blockchain Info (SuperAdmin only) */}
          {isSuperAdmin && transaction.received_tx_hash && (
            <div className="drawer-section">
              <div className="drawer-section-title">Blockchain</div>
              <div className="detail-grid">
                <span className="detail-label">TX Hash</span>
                <span className="detail-value">
                  {transaction.tx_explorer_url ? (
                    <a
                      href={transaction.tx_explorer_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={transaction.received_tx_hash}
                    >
                      {transaction.received_tx_hash.slice(0, 16)}...
                      <Icons.ExternalLink />
                    </a>
                  ) : (
                    <span className="mono" title={transaction.received_tx_hash}>
                      {transaction.received_tx_hash.slice(0, 16)}...
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Awaiting settlement: crypto is on-chain but USD not confirmed (BTC
              before the Montgomery webhook). Resolution stays locked — the POS
              resolve endpoint requires settlement data. */}
          {canResolve && !isMerchantAdmin && transaction.status === 'needs_resolution' && awaitingSettlement && (
            <div className="drawer-section resolution-section">
              <div className="drawer-section-title resolution-title">⏳ Awaiting Settlement</div>
              <p className="text-muted" style={{ margin: 0, fontSize: '13px' }}>
                Received {receivedCrypto.toFixed(getCryptoDecimals(transaction.crypto_currency))} {transaction.crypto_currency || ''}
                {estimatedReceivedUsd != null ? ` (~$${estimatedReceivedUsd.toFixed(2)} at quote rate)` : ''} on-chain,
                but the USD settlement has not been confirmed yet. Accept / deny options
                unlock once settlement arrives.
              </p>
            </div>
          )}

          {/* Payment Discrepancy Resolution Actions (crypto amount mismatch) */}
          {/* Note: Merchant admins see this in the unified payment summary above, so exclude them here */}
          {canResolve && !isMerchantAdmin && transaction.status === 'needs_resolution' && !awaitingSettlement && (
            <div className="drawer-section resolution-section">
              <div className="drawer-section-title resolution-title">
                {transaction.discrepancy_type === 'underfunded' ? '⚠️ Payment Discrepancy' : '💰 Overpayment Received'}
              </div>

              <div className="resolution-amounts">
                <div className="resolution-row">
                  <span className="resolution-label">Expected</span>
                  <span className="resolution-value">${paymentAmountUsd.toFixed(2)}</span>
                </div>
                <div className="resolution-row">
                  <span className="resolution-label">Settled</span>
                  <span className="resolution-value">${(getSettlementUsd(transaction) ?? 0).toFixed(2)}</span>
                </div>
                <div className={`resolution-row resolution-discrepancy ${transaction.discrepancy_type === 'underfunded' ? 'shortfall' : 'overage'}`}>
                  <span className="resolution-label">{transaction.discrepancy_type === 'underfunded' ? 'Shortfall' : 'Overage'}</span>
                  <span className="resolution-value">
                    {transaction.discrepancy_type === 'underfunded' ? '-' : '+'}
                    ${resolutionAmountUsd.toFixed(2)}
                  </span>
                </div>
              </div>

              {(feePreviewLoading || feePreviewError) && (
                <div
                  className={feePreviewError ? 'text-danger' : 'text-muted'}
                  style={{ marginBottom: '8px' }}
                >
                  {feePreviewError ? 'Fee preview unavailable. Please refresh.' : 'Loading fee preview...'}
                </div>
              )}

              {/* Fee Preview Sections */}
              {previewsAvailable && (() => {
                const receivedUsd = getSettlementUsd(transaction) ?? 0;

                if (transaction.discrepancy_type === 'underfunded') {
                  // Underfunded: Show single preview - "If You Accept"
                  const preview = acceptPreview;
                  return (
                    <>
                      <div className="resolution-fee-preview">
                        <div className="fee-preview-title" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '8px' }}>
                          If You Accept
                        </div>
                        <div className="resolution-row">
                          <span className="resolution-label">Received Amount</span>
                          <span className="resolution-value">${receivedUsd.toFixed(2)}</span>
                        </div>
                        <div className="resolution-row">
                          <span className="resolution-label">Fees (recalculated)</span>
                          <span className="resolution-value text-danger">-${preview.totalFees}</span>
                        </div>
                        <div className="resolution-row resolution-net-payout">
                          <span className="resolution-label"><strong>Net Payout</strong></span>
                          <span className={`resolution-value ${parseOptionalFloat(preview.netPayout) < -AMOUNT_DISPLAY_THRESHOLD ? 'resolution-highlight--negative' : 'resolution-highlight'}`}>
                            ${preview.netPayout}
                          </span>
                        </div>
                      </div>
                      <div className="resolution-fee-preview" style={{ background: 'rgba(245, 158, 11, 0.08)', borderColor: 'rgba(245, 158, 11, 0.2)' }}>
                        <div className="fee-preview-title" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '8px' }}>
                          If You Deny
                        </div>
                        <div className="resolution-row">
                          <span className="resolution-label">Platform Non-Refundable</span>
                          <span className="resolution-value text-danger">-${denyPreview.hardCosts}</span>
                        </div>
                        <div className="resolution-row resolution-net-payout">
                          <span className="resolution-label"><strong>Refund Due</strong></span>
                          <span className="resolution-value text-danger">-${denyRefundDueDisplay}</span>
                        </div>
                      </div>
                    </>
                  );
                }

                // Overfunded: Show two separate option boxes

                return (
                  <>
                    {/* Option A: Keep Full Amount */}
                    <div className="resolution-fee-preview" style={{ marginBottom: '12px' }}>
                      <div className="fee-preview-title" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '8px' }}>
                        Option A: Keep Full Amount
                      </div>
                      <div className="resolution-row">
                        <span className="resolution-label">Full Received</span>
                        <span className="resolution-value">${receivedUsd.toFixed(2)}</span>
                      </div>
                      <div className="resolution-row">
                        <span className="resolution-label">Fees (on ${receivedUsd.toFixed(2)})</span>
                        <span className="resolution-value text-danger">-${keepFullPreview.totalFees}</span>
                      </div>
                      <div className="resolution-row resolution-net-payout">
                        <span className="resolution-label"><strong>Net Payout</strong></span>
                        <span className={`resolution-value ${parseOptionalFloat(keepFullPreview.netPayout) < -AMOUNT_DISPLAY_THRESHOLD ? 'resolution-highlight--negative' : 'resolution-highlight'}`}>
                          ${keepFullPreview.netPayout}
                        </span>
                      </div>
                    </div>

                    {/* Option B: Refund Overage */}
                    <div className="resolution-fee-preview" style={{ background: 'rgba(245, 158, 11, 0.08)', borderColor: 'rgba(245, 158, 11, 0.2)' }}>
                      <div className="fee-preview-title" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '8px' }}>
                        Option B: Refund Overage
                      </div>
                      <div className="resolution-row">
                        <span className="resolution-label">Keep Amount</span>
                        <span className="resolution-value">${expectedAmountUsd.toFixed(2)}</span>
                      </div>
                      <div className="resolution-row">
                        <span className="resolution-label">Fees (on ${expectedAmountUsd.toFixed(2)})</span>
                        <span className="resolution-value text-danger">-${refundOveragePreview.totalFees}</span>
                      </div>
                      <div className="resolution-row">
                        <span className="resolution-label">Refund to Customer</span>
                        <span className="resolution-value text-danger">-${refundOverageActual.toFixed(2)}</span>
                      </div>
                      <div className="resolution-row resolution-net-payout">
                        <span className="resolution-label"><strong>Net Payout</strong></span>
                        <span className={`resolution-value ${parseOptionalFloat(refundOveragePreview.netPayout) < -AMOUNT_DISPLAY_THRESHOLD ? 'resolution-highlight--negative' : 'resolution-highlight'}`}>
                          ${refundOveragePreview.netPayout}
                        </span>
                      </div>
                    </div>
                  </>
                );
              })()}

              {resolutionSuccess ? (
                <div className="resolution-success">
                  ✓ Resolution saved successfully. Refreshing...
                </div>
              ) : (
                <>
                  {transaction.discrepancy_type === 'underfunded' && (
                    <div className="resolution-actions">
                      <button
                        className="resolve-btn resolve-btn--accept"
                        onClick={() => handleResolve('accept_partial', getSettlementUsd(transaction) ?? paymentAmountUsd)}
                        disabled={resolving || !previewsAvailable}
                      >
                        {resolving ? 'Processing...' : `Accept $${acceptPreview.netPayout}`}
                      </button>
                      <button
                        className="resolve-btn resolve-btn--deny"
                        onClick={() => handleResolve('deny')}
                        disabled={resolving || !previewsAvailable}
                      >
                        {resolving ? 'Processing...' : 'Deny & Refund'}
                      </button>
                      <p className="resolution-note" style={{ marginTop: '8px' }}>
                        Processing fees apply to whatever amount you accept. If denied, you must refund ${denyRefundDueDisplay} to the customer.
                      </p>
                    </div>
                  )}

                  {transaction.discrepancy_type === 'overfunded' && (
                    <div className="resolution-actions">
                      <button
                        className="resolve-btn resolve-btn--accept"
                        onClick={() => handleResolve('keep_full')}
                        disabled={resolving || !previewsAvailable}
                      >
                        {resolving ? 'Processing...' : `Keep Full $${keepFullPreview.netPayout}`}
                      </button>

                      <button
                        className="resolve-btn resolve-btn--refund"
                        onClick={() => handleResolve('partial_refund', expectedAmountUsd, refundOverageActual)}
                        disabled={resolving || !previewsAvailable}
                        style={{ marginTop: '8px' }}
                      >
                        {resolving ? 'Processing...' : `Refund $${refundOverageActual.toFixed(2)}`}
                      </button>
                    </div>
                  )}

                  {resolutionError && (
                    <div className="resolution-error">
                      {resolutionError}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Resolution Details - shows resolution history for any transaction that was resolved */}
          {/* Shows for paid with discrepancy, denied, refund_pending, OR refunded with resolution data */}
          {/* Merchant admins see resolution info in the unified view cards */}
          {!isMerchantAdmin && (transaction.resolution_action || transaction.resolution_notes) && (
            <div className="drawer-section">
              <div className="drawer-section-title">Resolution</div>
              <div
                className="resolution-info-card"
                style={{
                  background: '#f8fafc',
                  borderRadius: '8px',
                  padding: '12px 16px',
                }}
              >
                {transaction.resolution_action && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>Action</div>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>
                      {{
                        accept_partial: 'Accepted Partial Payment',
                        deny: 'Payment Denied',
                        keep_full: 'Kept Full Overpayment',
                        partial_refund: 'Refunded Overage',
                      }[transaction.resolution_action] || transaction.resolution_action}
                    </div>
                  </div>
                )}

                {transaction.resolved_at && (
                  <div style={{ marginBottom: transaction.resolution_notes ? '8px' : 0 }}>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>Resolved</div>
                    <div style={{ color: '#1e293b' }}>{moment(transaction.resolved_at).format('MMM DD, YYYY h:mm A')}</div>
                  </div>
                )}

                {transaction.resolution_notes && (
                  <div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>Notes</div>
                    <div style={{ color: '#1e293b', whiteSpace: 'pre-wrap' }}>{transaction.resolution_notes}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mark as Refunded Button - Not shown to merchant admins (they have refund UI in the unified view) */}
          {canShowRefundButton && !isMerchantAdmin && (
            <div className="drawer-section">
              <div className="drawer-section-title">Refund Actions</div>
              <button
                className="resolve-btn resolve-btn--refund"
                onClick={openRefundModal}
                style={{ width: '100%' }}
              >
                Mark as Refunded
              </button>
              <p className="resolution-note" style={{ marginTop: '8px' }}>
                Record a manual refund issued to the customer (store credit, check, PayPal, etc.)
              </p>
            </div>
          )}

          {/* Refund History - shows completed refunds with details */}
          {/* All users see refund history when there are multiple refunds or detailed info needed */}
          {transaction.refunds && transaction.refunds.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-title">Refund History</div>
              {transaction.refunds.map((refund, index) => {
                const methodLabels = {
                  bank_transfer: 'Bank Transfer',
                  check: 'Check',
                  store_credit: 'Store Credit',
                  paypal: 'PayPal',
                  crypto: 'Crypto',
                  ach: 'ACH',
                  woocommerce: 'WooCommerce',
                  other: 'Other',
                };
                const sourceLabels = {
                  denial: 'Payment Denial',
                  overage: 'Overage Refund',
                  manual: 'Manual Refund',
                };
                const statusLabel = refund.status === 'completed'
                  ? 'Refunded'
                  : refund.status === 'pending'
                    ? 'Refund Pending'
                    : 'Refund Cancelled';
                const statusColor = refund.status === 'completed'
                  ? { background: '#f0fdf4', border: '#86efac', text: '#166534' }
                  : refund.status === 'pending'
                    ? { background: '#fffbeb', border: '#fcd34d', text: '#92400e' }
                    : { background: '#fef2f2', border: '#fecaca', text: '#991b1b' };
                return (
                  <div
                    key={refund.id || index}
                    className="refund-history-item"
                    style={{
                      background: statusColor.background,
                      border: `1px solid ${statusColor.border}`,
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: index < transaction.refunds.length - 1 ? '8px' : 0,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 600, color: statusColor.text }}>
                        ${parseFloat(refund.refund_amount_usd || 0).toFixed(2)} {statusLabel}
                      </span>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {refund.completed_at
                          ? moment(refund.completed_at).format('MMM DD, YYYY')
                          : (refund.created_at ? moment(refund.created_at).format('MMM DD, YYYY') : '')}
                      </span>
                    </div>
                    <div className="detail-grid" style={{ fontSize: '13px' }}>
                      <span className="detail-label">Method</span>
                      <span className="detail-value">{methodLabels[refund.refund_method] || refund.refund_method || '—'}</span>

                      <span className="detail-label">Type</span>
                      <span className="detail-value">{sourceLabels[refund.refund_source] || refund.refund_source}</span>

                      {refund.reference_number && (
                        <>
                          <span className="detail-label">Reference</span>
                          <span className="detail-value mono">{refund.reference_number}</span>
                        </>
                      )}

                      {refund.refund_notes && (
                        <>
                          <span className="detail-label">Notes</span>
                          <span className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>{refund.refund_notes}</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Refund Modal */}
          {refundModalOpen && (
            <div className="modal-overlay" onClick={() => setRefundModalOpen(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{refundMode === 'resolution' ? 'Record Overage Refund' : 'Mark Payment as Refunded'}</h3>
                  <button className="modal-close" onClick={() => setRefundModalOpen(false)}>×</button>
                </div>
                <div className="modal-body">
                  {refundSuccess ? (
                    <div className="resolution-success">
                      ✓ {refundMode === 'resolution' ? 'Refund recorded and resolution saved' : 'Payment marked as refunded successfully'}
                    </div>
                  ) : refundStateLoading ? (
                    <div className="resolution-loading" style={{ textAlign: 'center', padding: '20px' }}>
                      Loading refund state...
                    </div>
                  ) : (
                    <>
                      {/* Show existing refunds from POS if any (query-first pattern) */}
                      {fetchedRefundState?.refunds?.length > 0 && (
                        <div className="existing-refunds-notice" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f4f8', borderRadius: '4px' }}>
                          <strong>Existing Refunds:</strong>
                          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                            {fetchedRefundState.refunds.map((r, i) => (
                              <li key={i} style={{ fontSize: '0.9em' }}>
                                {(() => {
                                  const statusLabel = r.status === 'completed'
                                    ? 'Refunded'
                                    : r.status === 'pending'
                                      ? 'Pending'
                                      : 'Cancelled';
                                  const sourceLabel = {
                                    denial: 'denial',
                                    overage: 'overage',
                                    manual: 'manual',
                                  }[r.refund_source] || r.refund_source;
                                  const methodLabel = r.refund_method || 'N/A';
                                  const dateLabel = r.completed_at
                                    ? moment(r.completed_at).format('MM/DD/YYYY')
                                    : (r.created_at ? moment(r.created_at).format('MM/DD/YYYY') : '');

                                  return `$${parseFloat(r.refund_amount_usd).toFixed(2)} ${statusLabel} (${sourceLabel})` +
                                    (r.status === 'completed' ? ` via ${methodLabel}` : '') +
                                    (dateLabel ? ` (${dateLabel})` : '');
                                })()}
                              </li>
                            ))}
                          </ul>
                          <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#666' }}>
                            {(() => {
                              const pendingTotal = parseFloat(fetchedRefundState.refund_due_usd || 0);
                              const completedTotal = parseFloat(fetchedRefundState.refunded_total_usd || 0);

                              if (pendingTotal > 0 && completedTotal === 0) {
                                return `Pending refund: $${pendingTotal.toFixed(2)}`;
                              }
                              if (pendingTotal > 0) {
                                return `Total refunded: $${completedTotal.toFixed(2)} (pending $${pendingTotal.toFixed(2)})`;
                              }
                              return `Total refunded: $${completedTotal.toFixed(2)}`;
                            })()}
                          </div>
                        </div>
                      )}
                      <div className="form-group">
                        <label>Refund Amount (USD)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={refundMode === 'resolution'
                            ? (refundDueUsd > AMOUNT_DISPLAY_THRESHOLD ? refundDueUsd : refundOverageActual)
                            : (fetchedRefundDueUsd && fetchedRefundDueUsd > 0
                                ? fetchedRefundDueUsd
                                : (fetchedRemainingUsd ?? remainingRefundable))}
                          value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          className="form-input"
                          placeholder="0.00"
                        />
                        <small className="form-hint">
                          Maximum: ${refundMode === 'resolution'
                            ? (refundDueUsd > AMOUNT_DISPLAY_THRESHOLD ? refundDueUsd : refundOverageActual).toFixed(2)
                            : ((fetchedRefundDueUsd && fetchedRefundDueUsd > 0
                                ? fetchedRefundDueUsd
                                : (fetchedRemainingUsd ?? remainingRefundable)).toFixed(2))}
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Refund Method *</label>
                        <select
                          value={refundMethod}
                          onChange={(e) => setRefundMethod(e.target.value)}
                          className="form-select"
                        >
                          <option value="">Select method...</option>
                          {REFUND_METHOD_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Reference Number (optional)</label>
                        <input
                          type="text"
                          value={refundReference}
                          onChange={(e) => setRefundReference(e.target.value)}
                          className="form-input"
                          placeholder="Check #, PayPal TX, etc."
                        />
                      </div>
                      <div className="form-group">
                        <label>Notes (optional)</label>
                        <textarea
                          value={refundNotes}
                          onChange={(e) => setRefundNotes(e.target.value)}
                          className="form-textarea"
                          placeholder="Additional notes about the refund..."
                          rows={3}
                        />
                      </div>
                      {refundError && (
                        <div className="resolution-error">
                          {refundError}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!refundSuccess && !refundStateLoading && (
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setRefundModalOpen(false)}
                      disabled={refundSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleMarkRefunded}
                      disabled={refundSubmitting || refundStateLoading}
                    >
                      {refundSubmitting
                        ? 'Processing...'
                        : (refundMode === 'resolution' ? 'Record Refund Complete' : 'Mark as Refunded')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timestamps - Not shown to merchant admins (they have a simplified footer in the unified view) */}
          {!isMerchantAdmin && (
            <div className="drawer-section">
              <div className="drawer-section-title">Timestamps</div>
              <div className="detail-grid">
                <span className="detail-label">Created</span>
                <span className="detail-value">{moment(transaction.created_at).format('MMM DD, YYYY HH:mm')}</span>

                {transaction.received_at && (
                  <>
                    <span className="detail-label">Paid At</span>
                    <span className="detail-value">{moment(transaction.received_at).format('MMM DD, YYYY HH:mm')}</span>
                  </>
                )}

                <span className="detail-label">Timeout</span>
                <span className="detail-value">{moment(transaction.timeout_at).format('MMM DD, YYYY HH:mm')}</span>

                {transaction.was_late_payment && (
                  <>
                    <span className="detail-label">Late Payment</span>
                    <span className="detail-value" style={{ color: '#f59e0b', fontWeight: 600 }}>
                      {transaction.late_by_formatted || `${Math.floor(transaction.late_by_seconds / 60)}m ${transaction.late_by_seconds % 60}s late`}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

TransactionDrawer.propTypes = {
  transaction: propTypes.object,
  isOpen: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
  user: propTypes.object,
  token: propTypes.string,
  onResolve: propTypes.func,
};

export default TransactionDrawer;
